import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompanySettings } from '@/hooks/use-settings';
import { generateArticleTicketPdf, DEFAULT_TICKET_ORDER } from '@erp/pdf';
import type { ArticleTicketBatch, TicketSettings } from '@erp/pdf';
import type { Article } from '@erp/data-access/interfaces';
import {
  Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  Input, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@erp/ui';
import { Printer, Download } from '@/components/icons';

interface ArticleTicketPrintDialogProps {
  articles: Article[];
  open: boolean;
  onClose: () => void;
}

export function ArticleTicketPrintDialog({ articles, open, onClose }: ArticleTicketPrintDialogProps) {
  const { t } = useTranslation();
  const { data: settings } = useCompanySettings();
  const [quantities, setQuantities] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    if (open) {
      const map = new Map<string, number>();
      articles.forEach((a) => map.set(a.id, quantities.get(a.id) ?? 1));
      setQuantities(map);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, articles.map((a) => a.id).join(',')]);

  const ticketSettings: TicketSettings = useMemo(() => ({
    widthMm: settings?.ticketWidthMm ?? 40,
    heightMm: settings?.ticketHeightMm ?? 20,
    showName: settings?.ticketShowName ?? true,
    showPrice: settings?.ticketShowPrice ?? true,
    showReference: settings?.ticketShowReference ?? true,
    showBarcode: settings?.ticketShowBarcode ?? true,
    showCompanyName: settings?.ticketShowCompanyName ?? false,
    currencySymbol: settings?.ticketCurrencySymbol ?? 'DT',
    elementOrder: settings?.ticketElementOrder ?? [...DEFAULT_TICKET_ORDER],
  }), [settings]);

  const totalTickets = useMemo(() => {
    let sum = 0;
    quantities.forEach((q) => { sum += Math.max(1, Math.floor(q || 1)); });
    return sum;
  }, [quantities]);

  const setQty = (id: string, value: string) => {
    const n = Math.max(1, Math.min(999, Math.floor(Number(value) || 1)));
    setQuantities((prev) => {
      const next = new Map(prev);
      next.set(id, n);
      return next;
    });
  };

  const buildBatches = (): ArticleTicketBatch[] => {
    return articles.map((a) => ({
      article: {
        name: a.name,
        reference: a.code,
        barcode: a.barcode ?? null,
        price: ticketSettings.showPrice
          ? (settings?.ticketPriceType === 'ht' ? a.salePriceHt : a.salePriceTtc)
          : 0,
      },
      quantity: Math.max(1, Math.floor(quantities.get(a.id) ?? 1)),
    }));
  };

  const handlePrint = (action: 'print' | 'download') => {
    if (!settings) return;
    generateArticleTicketPdf(
      {
        companyName: settings.companyName,
        taxId: settings.taxId,
        address: settings.address,
        phone: settings.phone,
        email: settings.email,
        logoUrl: settings.logoUrl,
      },
      buildBatches(),
      ticketSettings,
      action,
    );
    if (action === 'download') onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('articles.ticketDialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {t('articles.ticketDialog.format', {
                width: ticketSettings.widthMm,
                height: ticketSettings.heightMm,
                defaultValue: `${ticketSettings.widthMm} × ${ticketSettings.heightMm} mm`,
              })}
            </span>
            <span>{t('articles.ticketDialog.totalTickets', { count: totalTickets })}</span>
          </div>

          <div className="max-h-[50vh] overflow-auto border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('articles.ticketDialog.articleColumn')}</TableHead>
                  <TableHead>{t('articles.reference')}</TableHead>
                  <TableHead className="w-32 text-end">{t('articles.ticketDialog.quantityColumn')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {articles.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">{a.name}</TableCell>
                    <TableCell className="text-muted-foreground">{a.code}</TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={1}
                        max={999}
                        value={quantities.get(a.id) ?? 1}
                        onChange={(e) => setQty(a.id, e.target.value)}
                        className="w-20 ms-auto text-end"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="outline" onClick={() => handlePrint('download')}>
            <Download size={14} className="me-1.5" />
            {t('articles.ticketDialog.download')}
          </Button>
          <Button onClick={() => handlePrint('print')}>
            <Printer size={14} className="me-1.5" />
            {t('articles.ticketDialog.print')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
